// sw-listen — bridges the service-worker message channel into a
// window CustomEvent that React views (and any future subscriber)
// can attach to without knowing about the SW.
//
// Why the CustomEvent bounce? Two reasons:
//
//   1. Decoupling. InboxView shouldn't import navigator.serviceWorker
//      directly — that ties one specific view to one specific SW
//      message shape. The CustomEvent is a stable, narrow contract:
//      "a notice arrived, refetch if you care."
//   2. Multi-subscriber. A future inbox-badge in the chrome, or a
//      "live" indicator, just attaches another addEventListener on
//      window. No coordination with the SW or the listener wiring.
//
// The listener is idempotent: calling attachServiceWorkerListener()
// twice attaches the underlying navigator listener only once,
// regardless of how many React mounts call it.

import {
  isNoticeReceivedMessage,
  NOTICE_RECEIVED_EVENT,
} from "./sw-protocol";

let attached = false;

/**
 * Wire `navigator.serviceWorker.message` to dispatch a
 * `sms:notice-received` CustomEvent on window. Safe to call from any
 * mount point — only the first call talks to the browser; subsequent
 * calls are a no-op.
 *
 * Returns nothing — there's no cleanup. The listener lives for the
 * page lifetime, which matches the SW's lifetime.
 */
export function attachServiceWorkerListener(): void {
  if (attached) return;
  attached = true;
  if (typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = (event as MessageEvent).data;
    if (!isNoticeReceivedMessage(data)) return;
    window.dispatchEvent(
      new CustomEvent(NOTICE_RECEIVED_EVENT, { detail: data }),
    );
  });
}

/**
 * Test-only escape hatch. Resets the module-scoped `attached` flag
 * so the next call re-attaches the underlying listener. Exposed so
 * unit tests can exercise the idempotent path; not used by production.
 */
export function __resetForTests(): void {
  attached = false;
}
